import { useState, useEffect, useRef, useCallback } from "react";
import { useMediaQuery } from "@/lib/hooks";
import { useSearchParams, useNavigate } from "react-router-dom";
import CropStage from "../../user/generate-image/CropStage";
import {
  IconUser,
  IconMail,
  IconLock,
  IconCheck,
  IconChartBar,
  IconCalendar,
  IconFileDescription,
  IconCrown,
  IconUsers,
  IconPhoto,
  IconCamera,
  IconMapPin,
  IconBriefcase,
  IconBuilding,
  IconGavel,
  IconCreditCard,
  IconCoins,
  IconSparkles,
  IconSettings,
  IconShoppingCart,
  IconReceipt,
  IconRefresh,
  IconEye,
  IconEyeOff,
  IconBrush,
  IconClock,
  IconLink,
  IconWorld,
  IconPhone,
  IconTags,
  IconBuildingStore,
  IconPlus,
  IconTrash,
  IconAlertTriangle,
  IconEdit,
  IconMailCheck,
  IconCircleCheckFilled,
  IconAlertCircleFilled,
  IconX,
  IconZoomIn,
  IconZoomOut,
  IconCrop,
} from "@tabler/icons-react";
import Form, { Field, useForm } from "rc-field-form";
import { format, parse } from "date-fns";
import GeoCombobox from "../../../components/common/GeoCombobox";
import CountrySelect from "../../../components/common/CountrySelect";
import countryLabels from "react-phone-number-input/locale/en.json";
import countryFlags from "react-phone-number-input/flags";
import LowCreditBanner from "../../../components/common/LowCreditBanner";
import SelectField from "../../../components/common/SelectField";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Calendar } from "../../../components/ui/calendar";
import bdDistricts from "../../../data/bd-districts.json";
import bdUpazilas from "../../../data/bd-upazilas.json";
import { notifications } from "@/lib/notifications";
import { useAuthStore } from "../../../store/authStore";
import { creditParts } from "../../../utils/credit";
import { useOfficeSpaceStore } from "../../../store/officeSpaceStore";
import authApi from "../../../services/authApi";
import usageService from "../../../services/usageService";
import { buyCreditPathFor } from "../../../store/paymentConfigStore";
import { useTranslation } from "../../../i18n/hooks/useTranslation";
import BuyCreditTab from "./BuyCreditTab";
import PaymentHistoryTable from "./PaymentHistoryTable";
import paymentService from "../../../services/paymentService";
import { CREDIT_COSTS } from "../../../config/creditPackages";
import { ALL_DRESS_COLORS } from "../../../constants/dressColors";
import { ProfileCompletionCard } from "../../../components/common/ProfileCompletionBanner";
import EmailVerifyModal from "../../../components/common/EmailVerifyModal";

// ── Shared Tailwind class strings ──────────────────────────────────────────
const inputCls =
  "w-full px-3 py-2.5 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 disabled:opacity-50 disabled:cursor-not-allowed";

// ── Small reusable primitives ──────────────────────────────────────────────

function Spinner({ size = 16 }) {
  return (
    <svg
      className="animate-spin"
      style={{ width: size, height: size }}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}

function PasswordField({ value, onChange, placeholder, error, label, required: req, leftIcon }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
          {label} {req && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 z-10">
            {leftIcon}
          </span>
        )}
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`${inputCls} ${leftIcon ? "pl-8" : ""} pr-10 ${error ? "border-red-500 focus:ring-red-400" : ""}`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
        >
          {show ? <IconEyeOff size={16} /> : <IconEye size={16} />}
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

// rc-field-form compatible PasswordField (spread control props)
function RcPasswordField({ control, meta, label, placeholder, leftIcon, required: req }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
          {label} {req && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 z-10">
            {leftIcon}
          </span>
        )}
        <input
          {...control}
          type={show ? "text" : "password"}
          placeholder={placeholder}
          className={`${inputCls} ${leftIcon ? "pl-8" : ""} pr-10 ${meta?.errors?.[0] ? "border-red-500 focus:ring-red-400" : ""}`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
        >
          {show ? <IconEyeOff size={16} /> : <IconEye size={16} />}
        </button>
      </div>
      {meta?.errors?.[0] && <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>}
    </div>
  );
}

// Tooltip primitive
function Tip({ label, children }) {
  const [visible, setVisible] = useState(false);
  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded bg-zinc-800 text-white text-xs whitespace-nowrap z-50 pointer-events-none">
          {label}
        </div>
      )}
    </div>
  );
}

// Badge
function Badge({ children, color = "blue", size = "md" }) {
  const colorMap = {
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
    green: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
    red: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
    orange: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
    teal: "bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300",
    grape: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
    violet: "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300",
    gray: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    yellow: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
  };
  const sizeMap = { sm: "text-xs px-1.5 py-0.5", md: "text-xs px-2 py-1", lg: "text-sm px-2.5 py-1" };
  return (
    <span className={`inline-flex items-center font-semibold rounded-full ${colorMap[color] || colorMap.blue} ${sizeMap[size] || sizeMap.md}`}>
      {children}
    </span>
  );
}

// Alert
function Alert({ children, color = "blue", icon, title, variant = "light", withCloseButton, onClose }) {
  const colorMap = {
    blue: "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-200",
    red: "bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200",
    green: "bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200",
    teal: "bg-teal-50 border-teal-200 text-teal-800 dark:bg-teal-950 dark:border-teal-800 dark:text-teal-200",
    orange: "bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-950 dark:border-orange-800 dark:text-orange-200",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-950 dark:border-yellow-800 dark:text-yellow-200",
  };
  return (
    <div className={`flex gap-3 border rounded-md p-3 ${colorMap[color] || colorMap.blue}`}>
      {icon && <span className="flex-shrink-0 mt-0.5">{icon}</span>}
      <div className="flex-1 min-w-0">
        {title && <p className="font-semibold text-sm mb-0.5">{title}</p>}
        <div className="text-sm">{children}</div>
      </div>
      {withCloseButton && (
        <button type="button" onClick={onClose} className="flex-shrink-0 opacity-60 hover:opacity-100 text-lg leading-none">&times;</button>
      )}
    </div>
  );
}

// Skeleton
function Skeleton({ height = 20, width, radius = "md" }) {
  return (
    <div
      className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded"
      style={{ height, width: width || "100%" }}
    />
  );
}

// NumberInput primitive
function NumberInput({ value, onChange, min, max, step, style, className }) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange(Number(e.target.value))}
      style={style}
      className={`px-2 py-1 border border-zinc-300 dark:border-zinc-600 rounded text-center text-sm font-semibold bg-white dark:bg-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-orange-400 ${className || ""}`}
    />
  );
}

// Pagination
function Pagination({ value, onChange, total, size: _s }) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        disabled={value <= 1}
        onClick={() => onChange(value - 1)}
        className="px-2 py-1 rounded border border-zinc-300 dark:border-zinc-600 text-sm disabled:opacity-40 hover:bg-zinc-100 dark:hover:bg-zinc-700"
      >
        ‹
      </button>
      {Array.from({ length: total }, (_, i) => i + 1).map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          className={`px-2 py-1 rounded border text-sm ${
            p === value
              ? "bg-orange-500 text-white border-orange-500"
              : "border-zinc-300 dark:border-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700"
          }`}
        >
          {p}
        </button>
      ))}
      <button
        type="button"
        disabled={value >= total}
        onClick={() => onChange(value + 1)}
        className="px-2 py-1 rounded border border-zinc-300 dark:border-zinc-600 text-sm disabled:opacity-40 hover:bg-zinc-100 dark:hover:bg-zinc-700"
      >
        ›
      </button>
    </div>
  );
}

// ProgressBar
function ProgressBar({ value, color = "blue", size = "lg", label }) {
  const colorMap = {
    blue: "bg-blue-500", red: "bg-red-500", cyan: "bg-cyan-500",
    violet: "bg-violet-500", grape: "bg-purple-500", orange: "bg-orange-500",
    yellow: "bg-yellow-400", teal: "bg-teal-500",
  };
  const heightMap = { sm: "h-1.5", md: "h-2", lg: "h-3", xl: "h-4" };
  return (
    <div className={`w-full bg-zinc-200 dark:bg-zinc-700 rounded-full ${heightMap[size] || heightMap.md} overflow-hidden`}>
      <div
        className={`${colorMap[color] || "bg-blue-500"} h-full rounded-full transition-all duration-300`}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
  );
}

// Card
function Card({ children, className }) {
  return (
    <div className={`bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-700 p-4 ${className || ""}`}>
      {children}
    </div>
  );
}

// Divider
function Divider({ label, my = "lg" }) {
  const myMap = { sm: "my-3", md: "my-4", lg: "my-6", xl: "my-8" };
  return (
    <div className={`flex items-center gap-3 ${myMap[my] || "my-6"}`}>
      {label ? (
        <>
          <span className="text-xs font-medium text-zinc-400 dark:text-zinc-500 whitespace-nowrap">{label}</span>
          <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
        </>
      ) : (
        <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
      )}
    </div>
  );
}

// Avatar
function Avatar({ src, size = 80, children }) {
  if (src) {
    return (
      <img
        src={src}
        alt="avatar"
        className="rounded-full object-cover flex-shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-blue-500 flex items-center justify-center text-white font-bold flex-shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {children}
    </div>
  );
}

// ── Mantine Select drop-in (native <select>) ───────────────────────────────
function NativeSelect({ value, onChange, label, placeholder, data = [], clearable, error }) {
  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{label}</label>
      )}
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className={`${inputCls} ${error ? "border-red-500 focus:ring-red-400" : ""}`}
      >
        <option value="">{placeholder || "—"}</option>
        {data.map((d) => (
          <option key={d.value} value={d.value}>{d.label}</option>
        ))}
      </select>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ── Textarea ───────────────────────────────────────────────────────────────
function Textarea({ value, onChange, label, placeholder, error, minRows = 3 }) {
  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">{label}</label>
      )}
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={minRows}
        className={`${inputCls} resize-y ${error ? "border-red-500 focus:ring-red-400" : ""}`}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ── Tab bar (pill style) ──────────────────────────────────────────────────
function TabBar({ tabs, activeTab, onChange, isMobile }) {
  return (
    <div
      className="flex gap-2 overflow-x-auto pb-2 mb-4 [scrollbar-width:none] [ms-overflow-style:none]"
      style={{ flexWrap: "nowrap" }}
    >
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          className={`flex items-center gap-2 px-[15px] py-[9px] rounded-[11px] text-[13.5px] whitespace-nowrap flex-shrink-0 transition-colors border ${
            activeTab === tab.value
              ? "font-semibold bg-zinc-900 border-zinc-900 text-white dark:bg-zinc-100 dark:border-zinc-100 dark:text-zinc-900"
              : "font-medium bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
          }`}
        >
          {tab.icon && <span className="flex-shrink-0">{tab.icon}</span>}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

const CATEGORY_LABELS = {
  cyber_cafe: "সাইবার ক্যাফে / কম্পিউটার সেন্টার",
  photo_studio: "ফটো স্টুডিও",
  document_service: "ডকুমেন্ট সার্ভিস সেন্টার",
  print_shop: "প্রিন্ট ও ফটোকপি শপ",
  stationery: "স্টেশনারি ও অফিস সাপ্লাই",
  it_service: "আইটি সার্ভিস / সফটওয়্যার",
  notary_legal: "নোটারি / আইন সেবা",
  mobile_service: "মোবাইল সার্ভিস শপ",
  other: "অন্যান্য",
};
const OFF_DAY_LABELS = { sun: "রবি", mon: "সোম", tue: "মঙ্গল", wed: "বুধ", thu: "বৃহঃ", fri: "শুক্র", sat: "শনি" };

// Highlight for profile-completion fields: an unfilled (empty) field gets an amber ring so
// the user can see at a glance what still counts toward 100% / the bonus. Error > empty >
// normal. The empty check mirrors PROFILE_FIELDS in ProfileCompletionBanner (source of truth).
const isProfileFieldEmpty = (v) => v === null || v === undefined || String(v).trim() === "";
const fieldHighlightCls = (control, meta) =>
  meta?.errors?.[0]
    ? "border-red-500"
    : isProfileFieldEmpty(control?.value)
      ? "border-amber-400 dark:border-amber-500/70 ring-1 ring-amber-300/60 dark:ring-amber-500/30 bg-amber-50/50 dark:bg-amber-900/10"
      : "";

// Read-only summary of the user's personal profile. Shown first so the profile page
// surfaces the saved info at a glance; the Edit button flips over to the editable form.
function PersonalSummaryCard({ user, t, onEdit, onVerifyEmail }) {
  const emailVerified = !!user?.emailVerified;
  const bonusGiven = user?.settings?.profileCompletionBonusGiven === true;
  const isOperator = user?.role === "OPERATOR";
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  // Nudge to verify email only when it matters: an email exists, it's unverified, the
  // bonus hasn't been earned yet, and the user is bonus-eligible.
  const showEmailNudge = !!user?.email && !emailVerified && !bonusGiven && !isOperator && !isAdmin;
  const genderLabel = user?.gender
    ? t(`profile.${user.gender}`) || user.gender
    : null;
  const dobLabel = user?.dateOfBirth
    ? format(new Date(user.dateOfBirth), "dd MMMM yyyy")
    : null;
  const countryLabel = user?.country ? (countryLabels[user.country] || user.country) : null;
  const rows = [
    { label: t("auth.fullName"), value: user?.name },
    { label: t("auth.phoneNumber"), value: user?.phone },
    { id: "country", label: t("auth.country"), value: countryLabel },
    { id: "email", label: t("profile.email"), value: user?.email },
    { label: t("profile.profession"), value: user?.profession },
    { label: t("profile.gender"), value: genderLabel },
    { label: t("profile.dateOfBirth"), value: dobLabel },
    { label: t("profile.address"), value: user?.address },
    { label: t("profile.district"), value: user?.district },
    { label: t("profile.thana"), value: user?.thana },
    { label: t("profile.postcode"), value: user?.postcode },
    { label: t("profile.about"), value: user?.about, full: true },
  ];

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="flex h-10 w-10 flex-none items-center justify-center overflow-hidden rounded-xl bg-primary/10 text-primary">
            {user?.profilePicture
              ? <img src={user.profilePicture} alt="" className="h-full w-full rounded-xl bg-white object-cover" />
              : <IconUser size={20} stroke={1.7} />}
          </span>
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-zinc-900 dark:text-zinc-100">
              {user?.name || t("profile.personalInfo")}
            </p>
            {user?.profession && (
              <p className="text-xs text-zinc-500 dark:text-zinc-400">{user.profession}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex flex-none items-center gap-1.5 rounded-lg bg-primary hover:bg-primary/90 px-3.5 py-2 text-[13px] font-semibold text-white transition-colors"
        >
          <IconEdit size={15} stroke={1.8} />
          {t("profile.editInfo")}
        </button>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.map((r) => {
          const empty = r.value == null || r.value === "";
          // A filled-but-unverified email doesn't count toward completion either,
          // so its box gets highlighted (red, matching the "not verified" pill).
          const unverifiedEmail = r.id === "email" && !empty && !emailVerified;
          const CountryFlag = r.id === "country" && !empty && user?.country ? countryFlags[user.country] : null;
          return (
            <div
              key={r.label}
              className={`rounded-xl p-3 ${r.full ? "sm:col-span-2" : ""} ${
                empty
                  ? "bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/60"
                  : unverifiedEmail
                  ? "bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800/60"
                  : "bg-zinc-50 dark:bg-zinc-800/60"
              }`}
            >
              <dt className={`text-xs ${
                empty
                  ? "text-amber-600 dark:text-amber-400"
                  : unverifiedEmail
                  ? "text-red-600 dark:text-red-400"
                  : "text-zinc-500 dark:text-zinc-400"
              }`}>
                {r.label}
              </dt>
              <dd className={`mt-1 text-sm break-words ${
                empty
                  ? "italic text-amber-600/80 dark:text-amber-400/80"
                  : "font-medium text-zinc-800 dark:text-zinc-100"
              }`}>
                {empty ? (
                  t("profile.notProvided")
                ) : CountryFlag ? (
                  <span className="inline-flex items-center gap-1.5 align-middle">
                    <span className="w-5 h-4 flex-shrink-0 overflow-hidden rounded-[2px]">
                      <CountryFlag title={r.value} />
                    </span>
                    {r.value}
                  </span>
                ) : (
                  r.value
                )}
                {r.id === "email" && !empty && emailVerified && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 text-[10.5px] font-semibold align-middle">
                    <IconCircleCheckFilled size={12} /> {t("profile.emailVerified") || "যাচাইকৃত"}
                  </span>
                )}
                {r.id === "email" && !empty && !emailVerified && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 px-2 py-0.5 text-[10.5px] font-semibold align-middle">
                    <IconAlertCircleFilled size={12} /> {t("profile.emailNotVerified") || "যাচাই করা হয়নি"}
                  </span>
                )}
              </dd>
              {r.id === "email" && showEmailNudge && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-medium text-amber-600 dark:text-amber-500">
                    {t("profile.emailVerifyForBonus") || "বোনাস পেতে ইমেইল যাচাই করা আবশ্যক।"}
                  </span>
                  <button
                    type="button"
                    onClick={onVerifyEmail}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-primary/40 text-primary text-[11px] font-semibold rounded-lg hover:bg-primary/5 transition-colors"
                  >
                    <IconMailCheck size={13} /> {t("profile.verifyEmail") || "যাচাই করুন"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </dl>
    </div>
  );
}

// Read-only summary of the ACTIVE office space's shop / business ("Dokan") profile.
// Editing now lives in that office's settings page — this only mirrors it so the
// profile page still surfaces the currently-selected shop's info at a glance.
function ShopSummaryCard({ officeSpace, t, onManage, onCreate }) {
  // No selected shop means the owner has none yet (the layout auto-selects the first shop
  // whenever any exist) — so guide them to create one instead of the misleading "not selected".
  if (!officeSpace) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-dashed border-zinc-200 dark:border-zinc-700 p-8 text-center flex flex-col items-center gap-2">
        <div className="w-12 h-12 rounded-full bg-orange-50 dark:bg-orange-950/30 text-primary flex items-center justify-center">
          <IconBuildingStore size={24} stroke={1.7} />
        </div>
        <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">{t("officeSpace.noShopYetTitle")}</p>
        <p className="text-xs text-zinc-400 dark:text-zinc-500 max-w-xs">{t("officeSpace.noShopYetDesc")}</p>
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-amber-500 to-primary text-white hover:opacity-95 transition-opacity shadow-sm shadow-primary/30"
          >
            <IconPlus size={16} /> {t("officeSpace.createShopBtn")}
          </button>
        )}
      </div>
    );
  }
  const bp = officeSpace.businessProfile || {};
  const services = Array.isArray(bp.services) ? bp.services : [];
  const offDays = Array.isArray(bp.offDays) ? bp.offDays : [];
  const rows = [
    { label: t("profile.businessCategory") || "ব্যবসার ধরন", value: CATEGORY_LABELS[bp.businessCategory] || null },
    { label: t("profile.shopPhone") || "দোকানের ফোন নম্বর", value: bp.shopPhone },
    { label: t("profile.businessAddress") || "দোকানের ঠিকানা", value: bp.businessAddress },
    {
      label: t("profile.openTime") || "সময়",
      value: bp.openTime || bp.closeTime ? `${bp.openTime || "—"} – ${bp.closeTime || "—"}` : null,
    },
    { label: t("profile.offDays") || "সাপ্তাহিক বন্ধ", value: offDays.length ? offDays.map((d) => OFF_DAY_LABELS[d] || d).join(", ") : null },
    { label: t("profile.courtAddress") || "কোর্ট / নোটারি ঠিকানা", value: bp.courtAddress },
    { label: t("profile.businessAbout") || "দোকান সম্পর্কে", value: bp.businessAbout },
  ];

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="flex h-10 w-10 flex-none items-center justify-center overflow-hidden rounded-xl bg-primary/10 text-primary">
            {bp.shopLogo
              ? <img src={bp.shopLogo} alt="" className="h-full w-full rounded-xl bg-white object-contain" />
              : <IconBuildingStore size={20} stroke={1.7} />}
          </span>
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-zinc-900 dark:text-zinc-100">{officeSpace.name}</p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {bp.showInDirectory
                ? t("profile.inDirectory") || "ডিরেক্টরিতে প্রকাশিত"
                : t("profile.notInDirectory") || "ডিরেক্টরিতে নেই"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onManage}
          className="inline-flex flex-none items-center gap-1.5 rounded-lg bg-primary hover:bg-primary/90 px-3.5 py-2 text-[13px] font-semibold text-white transition-colors"
        >
          <IconSettings size={15} stroke={1.8} />
          {t("officeSpace.manageShop") || "দোকান সম্পাদনা করুন"}
        </button>
      </div>

      {services.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {services.map((s) => (
            <span key={s} className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 dark:bg-orange-950/30 text-orange-600 dark:text-orange-300 border border-orange-100 dark:border-orange-900">
              {s}
            </span>
          ))}
        </div>
      )}

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.filter((r) => r.value).map((r) => (
          <div key={r.label} className="rounded-xl bg-zinc-50 dark:bg-zinc-800/60 p-3">
            <dt className="text-xs text-zinc-500 dark:text-zinc-400">{r.label}</dt>
            <dd className="mt-1 text-sm font-medium text-zinc-800 dark:text-zinc-100 break-words">{r.value}</dd>
          </div>
        ))}
      </dl>

      {(bp.mapUrl || bp.facebookUrl) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {bp.mapUrl && (
            <a href={bp.mapUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800">
              <IconMapPin size={14} /> {t("profile.mapUrl") || "Google Maps"}
            </a>
          )}
          {bp.facebookUrl && (
            <a href={bp.facebookUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800">
              <IconLink size={14} /> Facebook
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function Profile() {
  const { user, updateProfile, getCurrentUser, updateSettings, logout } = useAuthStore();
  // Sync default-amount inputs when user settings load
  useEffect(() => {
    setImgAmountInput(String(user?.settings?.defaultImageGenAmount   ?? 60));
    setDocAmountInput(String(user?.settings?.defaultDocGenAmount      ?? 40));
    setAiTemplateAmountInput(String(user?.settings?.defaultAiTemplateAmount ?? 20));
    setManualEditAmountInput(String(user?.settings?.defaultManualEditAmount  ?? 50));
  }, [user?.settings?.defaultImageGenAmount, user?.settings?.defaultDocGenAmount, user?.settings?.defaultAiTemplateAmount, user?.settings?.defaultManualEditAmount]); // eslint-disable-line
  const { selectedOfficeSpace } = useOfficeSpaceStore();
  const isOperator = user?.role === "OPERATOR";
  // Admins have no shop/business ("Dokan") profile, so that sub-tab is hidden for them.
  const isAdmin = user?.role === "admin";
  const { t, i18n } = useTranslation();
  const isBn = i18n.language === "bn";
  const toBn = (num) => {
    if (!isBn) return String(num);
    return String(num).replace(/[0-9]/g, d => "০১২৩৪৫৬৭৮৯"[d]);
  };
  // While un-migrated with profile credit, show both old + new balances; otherwise the wallet.
  const { showBoth: showBothCredit, showLegacyOnly, legacy: oldCredit, wallet: newCredit } = creditParts(user);
  // Only qualify with the "পুরনো" (legacy) label when BOTH pools hold credit and the two
  // numbers need disambiguating. When the user has only one pool (legacy-only OR wallet-only),
  // it's just their balance — show the plain "ক্রেডিট" label + number, without the "পুরনো" word.
  const creditText = showBothCredit
    ? `${t('profile.creditOldLabel')} ${toBn(oldCredit)} • ${t('profile.creditNewLabel')} ${toBn(newCredit)}`
    : `${t('profile.creditNewLabel')} ${toBn(showLegacyOnly ? oldCredit : newCredit)}`;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();
  const [form] = useForm();
  const [passwordForm] = useForm();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  // Also sign out every other device when the password is changed (compromised-
  // password recovery). Checked by default — untick to keep other devices logged in.
  const [pwLogoutOthers, setPwLogoutOthers] = useState(true);
  // Live copy of the new-password field: rc-field-form doesn't re-render this page
  // on keystrokes, so the rules sidebar needs real state to turn green as you type.
  const [pwLive, setPwLive] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [showBonusBanner, setShowBonusBanner] = useState(false);
  const [bonusBannerAmount, setBonusBannerAmount] = useState(20);
  // Email verification (verify-then-save) modal.
  const [emailVerifyOpen, setEmailVerifyOpen] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState("");

  // Open the verify modal for the email currently typed in the field.
  const openEmailVerify = () => {
    const val = String(form.getFieldValue("email") || "").trim();
    // Strict pattern — rejects spaces, angle brackets, quotes, etc. (mirrors backend).
    if (!val || val.length > 254 || !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(val)) {
      form.setFields([{ name: "email", errors: [t("validation.invalidEmail") || "সঠিক ইমেইল ঠিকানা দিন"] }]);
      return;
    }
    setEmailToVerify(val);
    setEmailVerifyOpen(true);
  };

  // After the server verifies + saves the email: refresh the user (verified flag +
  // any new bonus credit) and celebrate if the bonus was just earned.
  const handleEmailVerified = async (updatedUser, bonusGranted) => {
    await getCurrentUser().catch(() => {});
    if (bonusGranted && !isOperator) {
      setBonusBannerAmount(updatedUser?.profileCompletionBonus ?? user?.profileCompletionBonus ?? 20);
      setShowBonusBanner(true);
    }
  };
  const [imgAmountInput, setImgAmountInput] = useState('');
  const [docAmountInput, setDocAmountInput] = useState('');
  const [aiTemplateAmountInput, setAiTemplateAmountInput] = useState('');
  const [manualEditAmountInput, setManualEditAmountInput] = useState('');
  const toEnglishDigits = (str) => str.replace(/[০-৯]/g, (ch) => '০১২৩৪৫৬৭৮৯'.indexOf(ch));
  const sanitizeAmountInput = (val) => val.replace(/[^0-9০-৯.]/g, '');
  const [amountsSaving, setAmountsSaving] = useState(false);
  const [profileSubTab, setProfileSubTab] = useState("personal");
  // Personal info shows a read-only summary first; editing is opt-in via the Edit button.
  const [personalEditMode, setPersonalEditMode] = useState(false);
  // Selected country in the edit form. Empty/unset OR "BD" → Bangladesh (geo dropdowns);
  // any other country → district/thana become free-text fields.
  const [personalCountry, setPersonalCountry] = useState(user?.country || "");
  const [searchParams, setSearchParams] = useSearchParams();

  // A single `tab` query param drives the visible section, so a reload or shared
  // link restores the same place:
  //   ?tab=profile          → personal information
  //   ?tab=shop             → shop / business
  //   ?tab=password-change  → change password
  // Devices tab is hidden: logins are effectively unlimited (100-device cap, per-app
  // sessions), so device management adds noise. DevicesTab.jsx and the backend
  // /api/auth/sessions endpoints remain; re-add "devices" here + the tab button to restore.
  const TAB_TO_SUBTAB = { profile: "personal", shop: "shop", "password-change": "password", "delete-account": "delete" };
  const SUBTAB_TO_TAB = { personal: "profile", shop: "shop", password: "password-change", delete: "delete-account" };
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "profile");

  // Sync section state from the URL (reload / back-forward / shared link).
  // Unknown tab values fall back to the default.
  useEffect(() => {
    const urlTab = searchParams.get("tab") || "profile";
    // Admins have no shop tab — bounce a direct ?tab=shop link back to personal info.
    if (!TAB_TO_SUBTAB[urlTab] || (isAdmin && urlTab === "shop")) {
      setSearchParams({ tab: "profile" }, { replace: true });
      return;
    }
    setActiveTab(urlTab);
    setProfileSubTab(TAB_TO_SUBTAB[urlTab]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdmin]);

  useEffect(() => {
    if (user?.role === "admin" && activeTab === "general") {
      setSearchParams({ tab: "profile" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, activeTab]);

  const handleSubTabChange = (sub) => {
    setProfileSubTab(sub);
    setSearchParams({ tab: SUBTAB_TO_TAB[sub] || "profile" });
  };

  // Usage stats state
  const [usageStats, setUsageStats] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // Credit history state
  const [creditData, setCreditData] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditPage, setCreditPage] = useState(1);
  const [creditStats, setCreditStats] = useState(null);
  const [creditCosts, setCreditCosts] = useState(null);
  const [creditConfig, setCreditConfig] = useState(null);
  const [hasPaymentHistory, setHasPaymentHistory] = useState(false);
  // Buy-credit CTAs point at the active gateway's tab (default SSLCommerz until config loads).
  const buyCreditPath = buyCreditPathFor(creditConfig?.activePaymentGateway, user?.country);

  const [defaultPrompts, setDefaultPrompts] = useState({
    removeGlasses: true, glowMakeup: true, showCustomInstruction: true,
    removeStrayHair: false, removeBeard: false, openEyes: false, keepSchoolDressDetails: false,
    removeTupi: false, straightenHead: false,
  });
  const [showSuitColorOptions, setShowSuitColorOptions] = useState(false);
  const [enabledDressStyles, setEnabledDressStyles] = useState(null);
  const [enabledDressColors, setEnabledDressColors] = useState(null);
  const [applyAiTemplateWatermark, setApplyAiTemplateWatermark] = useState(true);

  const DEFAULT_PRINT_SIZES = {
    passportSingleW: 41, passportSingleH: 50,
    passportDualW: 50, passportDualH: 41,
    stampSingleW: 20, stampSingleH: 25,
    stampDualW: 25, stampDualH: 20,
    size2RW: 64, size2RH: 89,
    size3RW: 89, size3RH: 127,
    size4RW: 102, size4RH: 152,
  };
  const [printSizes, setPrintSizes] = useState(DEFAULT_PRINT_SIZES);

  const ALL_DRESS_STYLES = [
    { value: "shirt",               label: t("imageGeneration.shirt"),              image: "/images/clothing-styles/shirt.svg" },
    { value: "t-shirt",             label: t("imageGeneration.tShirt"),             image: "/images/clothing-styles/t-shirt.svg" },
    { value: "suite",               label: t("imageGeneration.suite"),              image: "/images/clothing-styles/suite.svg" },
    { value: "suite-tieless",       label: t("imageGeneration.suiteTieless"),       image: "/images/clothing-styles/suite-tieless.svg" },
    { value: "panjbi",              label: t("imageGeneration.panjbi"),             image: "/images/clothing-styles/panjbi.svg" },
    { value: "tops",                label: t("imageGeneration.tops"),               image: "/images/clothing-styles/tops.svg" },
    { value: "round-collar-tshirt", label: t("imageGeneration.roundCollarTshirt"),  image: "/images/clothing-styles/round-collar-tshirt.svg" },
    { value: "sharee",              label: t("imageGeneration.sharee"),             image: "/images/clothing-styles/sharee.svg" },
    { value: "sharee-design",       label: t("imageGeneration.designSharee"),       image: "/images/clothing-styles/sharee-design.svg" },
    { value: "hizab",               label: t("imageGeneration.hizab"),              image: "/images/clothing-styles/hizab.svg" },
    { value: "kameez-hijab",        label: t("imageGeneration.kameezHijab"),        image: "/images/clothing-styles/kameez-hijab.svg" },
    { value: "shari-hijab",         label: t("imageGeneration.shariHijab"),         image: "/images/clothing-styles/shari-hijab.svg" },
    { value: "orna-kameez",         label: t("imageGeneration.ornaKameez"),         image: "/images/clothing-styles/orna-kameez.svg" },
    { value: "jacket",              label: t("imageGeneration.jacket"),             image: "/images/clothing-styles/jacket.svg" },
    { value: "abaya", label: t("imageGeneration.abaya"), image: "/images/clothing-styles/abaya.svg" },
    { value: "thobe", label: t("imageGeneration.thobe"), image: "/images/clothing-styles/thobe.svg" },
    { value: "thobe-ghutra", label: t("imageGeneration.thobeGhutra"), image: "/images/clothing-styles/thobe-ghutra.svg" },
    { value: "nehru-jacket", label: t("imageGeneration.nehruJacket"), image: "/images/clothing-styles/nehru-jacket.svg" },
    { value: "hijab-formal", label: t("imageGeneration.hijabFormalWear"), image: "/images/clothing-styles/hijab-formal.svg" },
  ];

  useEffect(() => {
    if (user?.defaultPrompts) {
      setDefaultPrompts(prev => ({ ...prev, ...user.defaultPrompts }));
    }
    if (user?.settings?.showSuitColorOptions !== undefined) {
      setShowSuitColorOptions(!!user.settings.showSuitColorOptions);
    }
    if (user?.settings?.enabledDressStyles !== undefined) {
      setEnabledDressStyles(user.settings.enabledDressStyles?.length ? user.settings.enabledDressStyles : null);
    }
    if (user?.settings?.enabledDressColors !== undefined) {
      setEnabledDressColors(user.settings.enabledDressColors?.length ? user.settings.enabledDressColors : null);
    }
    if (user?.settings?.printSizes) {
      setPrintSizes(prev => ({ ...prev, ...user.settings.printSizes }));
    }
    if (user?.settings?.applyAiTemplateWatermark !== undefined) {
      setApplyAiTemplateWatermark(!!user.settings.applyAiTemplateWatermark);
    }
  }, [user?.id]);

  // Profile picture upload state
  const [pictureLoading, setPictureLoading] = useState(false);
  const [picturePreview, setPicturePreview] = useState(null);

  // Crop modal state — the crop UI itself is the shared fullscreen CropStage
  // (the same editor used by the photo-edit modules).
  const fileInputRef = useRef(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const pendingFileRef = useRef(null); // original selected file — "skip crop" uploads it as-is

  const doUpload = useCallback(async (blob) => {
    if (!blob || blob.size === 0) {
      notifications.show({ title: t("common.error"), message: t("profile.pictureUploadFailed") || "Failed to process image", color: "red" });
      return;
    }
    const ext = blob.type === "image/jpeg" ? "jpg" : "webp";
    const file = new File([blob], `profile.${ext}`, { type: blob.type });
    const objectUrl = URL.createObjectURL(blob);
    setPicturePreview(objectUrl);
    try {
      setPictureLoading(true);
      const response = await authApi.uploadProfilePicture(file);
      if (response.success) {
        await updateProfile(response.data);
        notifications.show({ title: t("common.success"), message: t("profile.pictureUpdated") || "Profile picture updated", color: "green", icon: <IconCheck size={16} /> });
      }
    } catch (error) {
      console.error("Picture upload error:", error);
      setPicturePreview(null);
      notifications.show({ title: t("common.error"), message: t("profile.pictureUploadFailed") || "Failed to upload picture", color: "red" });
    } finally {
      setPictureLoading(false);
      URL.revokeObjectURL(objectUrl);
    }
  }, [t, updateProfile]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    pendingFileRef.current = file;
    const src = URL.createObjectURL(file);
    setCropSrc(src);
    setCropModalOpen(true);
  };

  const handleCropModalClose = () => {
    setCropModalOpen(false);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    pendingFileRef.current = null;
  };

  // CropStage hands back a compressed square JPEG File — upload it directly.
  const handleCropApply = async (file) => {
    setCropModalOpen(false);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    await doUpload(file);
  };

  const handleCropSkip = async () => {
    const original = pendingFileRef.current;
    setCropModalOpen(false);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    if (original) await doUpload(original);
  };

  const handleCropError = () => {
    handleCropModalClose();
    notifications.show({ title: t("common.error"), message: t("profile.pictureUploadFailed") || "Failed to process image", color: "red" });
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        await getCurrentUser();
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
      }
    };
    fetchUser();
    usageService.getCreditConfig().then(setCreditConfig).catch(() => {});
  }, [getCurrentUser]);

  useEffect(() => {
    const checkPayments = async () => {
      if (user?.role === "OPERATOR") {
        if (!selectedOfficeSpace?.id) return;
        const result = await paymentService.getOwnerHasPayment(selectedOfficeSpace.id);
        setHasPaymentHistory(!!result?.hasPayment);
        return;
      }
      const data = await paymentService.getHistory({ page: 1, limit: 50 });
      const transactions = data?.transactions || [];
      setHasPaymentHistory(transactions.some(tx => tx.status === "SUCCESS"));
    };
    checkPayments().catch(() => {});
  }, [user?.id, user?.role, selectedOfficeSpace?.id]);

  useEffect(() => {
    if (activeTab === "usages") {
      const fetchStats = async () => {
        try {
          setUsageLoading(true);
          const data = await usageService.getUserUsageStats();
          setUsageStats(data);
        } catch (error) {
          console.error("Failed to fetch usage stats:", error);
        } finally {
          setUsageLoading(false);
        }
      };
      fetchStats();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "credits") {
      const fetchCredits = async () => {
        try {
          setCreditLoading(true);
          const [data, stats, costs] = await Promise.all([
            usageService.getCreditHistory({ page: creditPage, days: 7 }),
            usageService.getCreditStats(),
            usageService.getCreditCosts(),
          ]);
          setCreditData(data);
          setCreditStats(stats);
          setCreditCosts(costs);
        } catch (error) {
          console.error("Failed to fetch credit history:", error);
          notifications.show({ title: t("common.error"), message: t("profile.creditLoadFailed"), color: "red" });
        } finally {
          setCreditLoading(false);
        }
      };
      fetchCredits();
    }
  }, [activeTab, creditPage]);

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const handleProfileUpdate = async (values) => {
    try {
      setLoading(true);
      const textFields = ["name", "email", "address", "district", "thana", "postcode",
        "profession", "about"];
      const fieldErrors = [];
      for (const field of textFields) {
        const raw = values[field];
        if (!raw) {
          if (field === "name") fieldErrors.push({ name: field, errors: [t("profile.nameMinLength")] });
          continue;
        }
        const trimmed = raw.trim();
        if (trimmed.length > 0 && trimmed.length < 2) {
          fieldErrors.push({ name: field, errors: [t("profile.fieldMinLength")] });
        }
      }
      if (fieldErrors.length > 0) {
        form.setFields(fieldErrors);
        notifications.show({
          title: t("common.validationError"),
          message: fieldErrors.length === 1 && fieldErrors[0].name === "name"
            ? t("profile.nameMinLength")
            : t("profile.fieldMinLength"),
          color: "red",
        });
        return;
      }
      const payload = {
        name: values.name.trim(),
        country: values.country || null,
        email: values.email?.trim() || null,
        address: values.address?.trim() || null,
        // Omit (don't null-out) district/thana when empty so an unhydrated or
        // untouched field never erases the value saved at registration.
        ...(values.district?.trim() ? { district: values.district.trim() } : {}),
        ...(values.thana?.trim() ? { thana: values.thana.trim() } : {}),
        postcode: values.postcode?.trim() || null,
        gender: values.gender || null,
        about: values.about?.trim() || null,
        profession: values.profession?.trim() || null,
        dateOfBirth: values.dateOfBirth || null,
        defaultPrompts,
      };
      const response = await authApi.updateProfile(payload);
      if (response.success) {
        await updateProfile(response.data);
        if (response.profileCompletionBonus && !isOperator) {
          const bonusAmount = response.data?.profileCompletionBonus ?? 20;
          setBonusBannerAmount(bonusAmount);
          setShowBonusBanner(true);
          notifications.show({
            title: t("profileCompletion.bonusBannerTitle", { amount: bonusAmount }),
            message: t("profileCompletion.bonusBannerMessage", { amount: bonusAmount }),
            color: "green",
            icon: <IconSparkles size={16} />,
            autoClose: 8000,
          });
        }
        notifications.show({ title: t("common.success"), message: t("profile.profileUpdated"), color: "green", icon: <IconCheck size={16} /> });
        setPersonalEditMode(false);
      }
    } catch (error) {
      console.error("Update error:", error);
      // Email already used by another account, or a disposable email → show on the field.
      if (error?.code === "EMAIL_ALREADY_EXISTS" || /email is already in use/i.test(error?.message || "")) {
        form.setFields([{ name: "email", errors: [t("validation.emailAlreadyExists")] }]);
        notifications.show({ title: t("common.error"), message: t("validation.emailAlreadyExists"), color: "red" });
      } else if (/disposable|temporary email/i.test(error?.message || "")) {
        form.setFields([{ name: "email", errors: [t("validation.disposableEmail")] }]);
        notifications.show({ title: t("common.error"), message: t("validation.disposableEmail"), color: "red" });
      } else {
        notifications.show({ title: t("common.error"), message: t("profile.failedToUpdate") || error.message, color: "red" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (values) => {
    try {
      setPasswordLoading(true);
      // Clear previous field errors
      passwordForm.setFields([
        { name: "currentPassword", errors: [] },
        { name: "newPassword", errors: [] },
        { name: "confirmPassword", errors: [] },
      ]);
      if (!values.currentPassword) {
        passwordForm.setFields([{ name: "currentPassword", errors: [t("profile.currentPasswordRequired")] }]);
        return;
      }
      const pwErrors = [];
      if (!values.newPassword || values.newPassword.length < 8) {
        pwErrors.push(t("profile.passwordRule1"));
      }
      if (values.newPassword && (!/[a-z]/.test(values.newPassword) || !/[A-Z]/.test(values.newPassword))) {
        pwErrors.push(t("profile.passwordRule2"));
      }
      if (values.newPassword && !/[0-9]/.test(values.newPassword)) {
        pwErrors.push(t("profile.passwordRule3"));
      }
      if (values.newPassword && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(values.newPassword)) {
        pwErrors.push(t("profile.passwordRule4"));
      }
      if (pwErrors.length > 0) {
        passwordForm.setFields([{ name: "newPassword", errors: pwErrors }]);
        return;
      }
      if (values.newPassword !== values.confirmPassword) {
        passwordForm.setFields([{ name: "confirmPassword", errors: [t("validation.passwordMismatch")] }]);
        return;
      }
      const response = await authApi.changePassword({ currentPassword: values.currentPassword, newPassword: values.newPassword, logoutOthers: pwLogoutOthers });
      if (response.success) {
        // If backend returns a new token after password change, update it to prevent 401 logout
        if (response.data?.token) {
          useAuthStore.setState({ token: response.data.token });
        }
        passwordForm.resetFields();
        setPwLogoutOthers(true);
        setPwLive("");
        notifications.show({ title: t("common.success"), message: t("profile.passwordChanged"), color: "green", icon: <IconCheck size={16} /> });
      }
    } catch (error) {
      console.error("Password change error:", error);
      notifications.show({ title: t("common.error"), message: t("profile.failedToChangePassword") || error.message, color: "red" });
    } finally {
      setPasswordLoading(false);
    }
  };

  const openDeleteModal = () => {
    setDeletePassword("");
    setDeleteError("");
    setDeleteModalOpen(true);
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError(t("profile.deleteAccountPasswordRequired") || "Password is required");
      return;
    }
    setDeleteError("");
    setDeleteLoading(true);
    try {
      await authApi.deleteAccount({ password: deletePassword });
      notifications.show({
        title: t("common.success"),
        message: t("profile.deleteAccountSuccess") || "Your account has been deleted.",
        color: "green",
        icon: <IconCheck size={16} />,
      });
      // Clear the session and send the user to the login page.
      setDeleteModalOpen(false);
      logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Delete account error:", error);
      setDeleteError(error.message || t("profile.deleteAccountFailed") || "Failed to delete account");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUpgradeToPro = async () => {
    try {
      if (!user?.phone) {
        notifications.show({ title: t("common.error"), message: t("profile.profileLoadFailed"), color: "red" });
        return;
      }
      await authApi.updatePlan({ phone: user.phone, plan: "pro" });
      await getCurrentUser();
      const updatedStats = await usageService.getUserUsageStats();
      setUsageStats(updatedStats);
      notifications.show({ title: t("common.success"), message: t("profile.upgradedToPro"), color: "green", icon: <IconCheck size={16} /> });
    } catch (error) {
      console.error("Plan upgrade failed:", error);
      notifications.show({ title: t("common.error"), message: error.message || t("profile.planUpdateFailed"), color: "red" });
    }
  };

  const renderUsageContent = () => {
    if (usageLoading) {
      return (
        <div className="flex flex-col gap-4">
          <Skeleton height={50} />
          <Skeleton height={120} />
          <Skeleton height={50} />
        </div>
      );
    }
    if (!usageStats) return null;

    const {
      plan, documentsGenerated, limit, limitResetDate, isLimitReached,
      operatorCount, dailyDocumentsGenerated = 0,
      dailyDocumentLimit = "unlimited", isDailyDocumentLimitReached = false,
      dailyImageGenerationCount = 0,
      dailyImageLimit = "unlimited", isDailyImageLimitReached = false,
    } = usageStats;

    const isPro = plan === "pro";
    const isUnlimited = limit === "unlimited";
    const isDailyUnlimited = dailyDocumentLimit === "unlimited";

    const percentage = isUnlimited ? 0 : Math.min((documentsGenerated / limit) * 100, 100);
    const dailyDocumentPercentage = isDailyUnlimited ? 0 : Math.min((dailyDocumentsGenerated / dailyDocumentLimit) * 100, 100);
    const dailyImagePercentage = dailyImageLimit === "unlimited" ? 0 : Math.min((dailyImageGenerationCount / dailyImageLimit) * 100, 100);

    const formattedResetDate = limitResetDate ? new Date(limitResetDate).toLocaleDateString("bn-BD", { year: "numeric", month: "long", day: "numeric" }) : "";

    return (
      <div className="flex flex-col gap-6">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <Card>
            <div className="flex justify-between items-start mb-2">
              <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{t("profile.currentPlan")}</span>
              <span className={`p-1.5 rounded-lg ${isPro ? "bg-purple-100 dark:bg-purple-900 text-purple-600" : "bg-blue-100 dark:bg-blue-900 text-blue-600"}`}>
                <IconCrown size={20} />
              </span>
            </div>
            <div className="flex items-end gap-2">
              <span className="text-xl font-bold capitalize text-zinc-800 dark:text-zinc-100">{plan}</span>
              {!isPro && (
                <button type="button" onClick={handleUpgradeToPro} className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 font-medium">
                  {t("profile.upgrade")}
                </button>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex justify-between items-start mb-2">
              <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{t("profile.resetDate")}</span>
              <span className="p-1.5 rounded-lg bg-orange-100 dark:bg-orange-900 text-orange-600">
                <IconCalendar size={20} />
              </span>
            </div>
            {isUnlimited && isDailyUnlimited ? (
              <span className="text-xl font-bold text-zinc-800 dark:text-zinc-100">{t("profile.notApplicable")}</span>
            ) : (
              !isUnlimited && (
                <div>
                  <p className="text-xs font-medium text-zinc-400 mt-1">{t("profile.monthlyReset")}</p>
                  <p className="text-base font-semibold text-zinc-800 dark:text-zinc-100">{formattedResetDate}</p>
                </div>
              )
            )}
          </Card>

          <Card>
            <div className="flex justify-between items-start mb-2">
              <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{t("profile.operatorCount")}</span>
              <span className="p-1.5 rounded-lg bg-teal-100 dark:bg-teal-900 text-teal-600">
                <IconUsers size={20} />
              </span>
            </div>
            <span className="text-xl font-bold text-zinc-800 dark:text-zinc-100">{operatorCount || 0}</span>
            <p className="text-xs text-zinc-400 mt-1">{t("profile.acrossAllOfficeSpaces")}</p>
          </Card>
        </div>

        {/* Document generation */}
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <span className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900 text-blue-600"><IconFileDescription size={20} /></span>
            <span className="font-medium text-zinc-800 dark:text-zinc-100">{t("profile.documentGeneration")}</span>
          </div>
          {(!isDailyUnlimited || !isUnlimited) && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              {!isDailyUnlimited && (
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">{t("profile.dailyLimit")}</span>
                    <span className="text-xs font-semibold">{dailyDocumentsGenerated}/{dailyDocumentLimit}</span>
                  </div>
                  <ProgressBar value={dailyDocumentPercentage} color={dailyDocumentPercentage >= 100 ? "red" : "cyan"} size="lg" />
                  <p className="text-xs text-zinc-400 text-center mt-1">{t("profile.todayPercent", { percent: Math.round(dailyDocumentPercentage) })}</p>
                </div>
              )}
              {!isUnlimited && (
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">{t("profile.monthlyLimit")}</span>
                    <span className="text-xs font-semibold">{documentsGenerated}/{limit}</span>
                  </div>
                  <ProgressBar value={percentage} color={percentage >= 100 ? "red" : "blue"} size="lg" />
                  <p className="text-xs text-zinc-400 text-center mt-1">{t("profile.thisMonthPercent", { percent: Math.round(percentage) })}</p>
                </div>
              )}
            </div>
          )}
          {isUnlimited && isDailyUnlimited && (
            <Alert icon={<IconCheck size={16} />} title={t("profile.unlimitedAccess")} color="teal">
              {t("profile.unlimitedDocumentsMessage")}
            </Alert>
          )}
        </Card>

        {/* Image generation */}
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <span className="p-1.5 rounded-lg bg-orange-100 dark:bg-orange-900 text-orange-600"><IconPhoto size={20} /></span>
            <span className="font-medium text-zinc-800 dark:text-zinc-100">{t("profile.imageGeneration")}</span>
          </div>
          {(dailyImageLimit !== "unlimited" || !isUnlimited) && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              {dailyImageLimit !== "unlimited" && (
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">{t("profile.dailyLimit")}</span>
                    <span className="text-xs font-semibold">{dailyImageGenerationCount}/{dailyImageLimit}</span>
                  </div>
                  <ProgressBar value={dailyImagePercentage} color={dailyImagePercentage >= 100 ? "red" : "yellow"} size="lg" />
                  <p className="text-xs text-zinc-400 text-center mt-1">{t("profile.todayPercent", { percent: Math.round(dailyImagePercentage) })}</p>
                </div>
              )}
              {!isUnlimited && (() => {
                const imgCount = usageStats.imageGenerationCount || 0;
                const imgLimit = usageStats.imageGenerationLimit || limit;
                const imgPct = Math.min((imgCount / imgLimit) * 100, 100);
                return (
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{t("profile.monthlyLimit")}</span>
                      <span className="text-xs font-semibold">{imgCount}/{imgLimit}</span>
                    </div>
                    <ProgressBar value={imgPct} color={imgCount >= imgLimit ? "red" : "orange"} size="lg" />
                    <p className="text-xs text-zinc-400 text-center mt-1">{t("profile.thisMonthPercent", { percent: Math.round(imgPct) })}</p>
                  </div>
                );
              })()}
            </div>
          )}
          {isUnlimited && dailyImageLimit === "unlimited" && (
            <Alert icon={<IconCheck size={16} />} title={t("profile.unlimitedAccess")} color="teal">
              {t("profile.unlimitedImagesMessage")}
            </Alert>
          )}
        </Card>

        <Alert icon={<IconCalendar size={16} />} title={t("profile.importantInfo")} color="blue">
          {t("profile.usageLimitInfo")}
        </Alert>
      </div>
    );
  };

  // Profile page only has the profile tab now — settings moved to /settings
  const tabList = [];

  return (
    <div className="font-hind pt-2 sm:pt-6 px-1 sm:px-2 pb-14">
      <div className="mb-[18px]">
        <h1 className="m-0 text-[26px] sm:text-[30px] font-bold text-zinc-800 dark:text-zinc-100">
          {t("profile.myProfile")}
        </h1>
        {t("profile.myProfileSubtitle") && (
          <div className="text-[14.5px] text-zinc-500 dark:text-zinc-400 mt-1">
            {t("profile.myProfileSubtitle")}
          </div>
        )}
      </div>

      {/* Profile Info Header */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm p-4 sm:p-5 mb-3 sm:mb-4">
        <div className="flex items-center gap-3 sm:gap-4 flex-nowrap">
          <div
            className="relative inline-block flex-shrink-0 group cursor-pointer"
            onClick={() => !pictureLoading && fileInputRef.current?.click()}
            title={t("profile.changePicture") || "Change picture"}
          >
            <div className="w-[56px] h-[56px] sm:w-[76px] sm:h-[76px] rounded-full ring-2 ring-orange-100 dark:ring-zinc-700 overflow-hidden bg-orange-50 dark:bg-zinc-800 flex-shrink-0">
              {(picturePreview || user?.profilePicture) ? (
                <img src={picturePreview || user?.profilePicture} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-primary font-bold text-lg sm:text-2xl">
                  {getInitials(user?.name)}
                </div>
              )}
              {/* Hover overlay */}
              <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100">
                {pictureLoading ? <Spinner size={18} /> : <IconCamera size={isMobile ? 16 : 22} className="text-white" />}
              </div>
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center border-2 border-white dark:border-zinc-900 shadow-sm">
              <IconCamera size={12} />
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-bold truncate ${isMobile ? "text-base" : "text-xl"} text-zinc-800 dark:text-zinc-100`}>
                {user?.name}
              </span>
              {user?.profession && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-50 dark:bg-orange-950/30 text-primary border border-orange-200 dark:border-orange-900/50">
                  {user.profession}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 sm:gap-4 mt-1.5 flex-wrap text-zinc-500 dark:text-zinc-400">
              {user?.phone && (
                <span className="flex items-center gap-1.5 text-sm">
                  <IconPhone size={14} className="flex-shrink-0 text-zinc-400" />
                  {user.phone}
                </span>
              )}
              {user?.email && (
                <span className="flex items-center gap-1.5 text-sm">
                  <IconMail size={14} className="flex-shrink-0 text-zinc-400" />
                  {user.email}
                </span>
              )}
              {user?.country && countryFlags[user.country] && (() => {
                const HeaderFlag = countryFlags[user.country];
                const headerCountryName = countryLabels[user.country] || user.country;
                return (
                  <span className="flex items-center gap-1.5 text-sm">
                    <span className="w-5 h-4 flex-shrink-0 overflow-hidden rounded-[2px]">
                      <HeaderFlag title={headerCountryName} />
                    </span>
                    {headerCountryName}
                  </span>
                );
              })()}
            </div>
          </div>
          {/* Credit balance on far right */}
          {user?.role === "OWNER" && !isMobile && (
            <button
              onClick={() => navigate(buyCreditPath)}
              className="flex items-center gap-2.5 flex-shrink-0 rounded-xl bg-orange-50 dark:bg-orange-950/20 border border-orange-100 dark:border-orange-900/40 px-4 py-2.5 hover:bg-orange-100 dark:hover:bg-orange-950/40 transition-colors"
            >
              <span className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <IconCoins size={20} />
              </span>
              <div className="text-right">
                <div className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 leading-none mb-1">{t("profile.creditBalance") || "ক্রেডিট ব্যালেন্স"}</div>
                <div className={`${showBothCredit ? "text-sm" : "text-xl"} font-extrabold text-primary tabular-nums leading-none`}>{creditText}</div>
              </div>
            </button>
          )}
        </div>
        {/* Mobile credit badge */}
        {user?.role === "OWNER" && isMobile && (
          <button
            onClick={() => navigate(buyCreditPath)}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold bg-orange-50 dark:bg-orange-950/20 border border-orange-100 dark:border-orange-900/40 text-primary hover:bg-orange-100 transition-colors"
          >
            <IconCoins size={15} />
            {t("profile.creditBalance") || "ক্রেডিট"}: {creditText}
          </button>
        )}
      </div>

      {/* Low credit alert */}
      {user?.role === "OWNER" && (
        <LowCreditBanner credit={user?.credit ?? 0} className="mb-3" />
      )}

      {/* Profile completion — shown on the personal AND shop tabs; hidden for admins.
          Shared card (also mounted on the first shop's settings page) that names the
          exact missing fields and self-hides at 100%. */}
      {(profileSubTab === "personal" || profileSubTab === "shop") && !isAdmin && user?.role !== "superadmin" && (
        <ProfileCompletionCard />
      )}

      {/* Bonus banner */}
      {showBonusBanner && (
        <div className="mb-4">
          <Alert
            icon={<IconSparkles size={18} />}
            title={t("profileCompletion.bonusBannerTitle", { amount: bonusBannerAmount })}
            color="teal"
            withCloseButton
            onClose={() => setShowBonusBanner(false)}
          >
            {t("profileCompletion.bonusBannerMessage", { amount: bonusBannerAmount })}
          </Alert>
        </div>
      )}

      {/* Email verification modal (verify-then-save) */}
      <EmailVerifyModal
        open={emailVerifyOpen}
        onOpenChange={setEmailVerifyOpen}
        email={emailToVerify}
        onVerified={handleEmailVerified}
      />

      {/* ── Profile content ── */}
      {(
        <div>
          {/* Sub-tab switcher */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-4 [scrollbar-width:none] [ms-overflow-style:none]">
            <button
              type="button"
              onClick={() => handleSubTabChange("personal")}
              className={`px-[15px] py-[9px] rounded-[11px] text-[13.5px] whitespace-nowrap flex-shrink-0 transition-colors border ${
                profileSubTab === "personal"
                  ? "font-semibold bg-zinc-900 border-zinc-900 text-white dark:bg-zinc-100 dark:border-zinc-100 dark:text-zinc-900"
                  : "font-medium bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              }`}
            >
              {t("profile.personalInformation")}
            </button>
            {!isOperator && !isAdmin && (
              <button
                type="button"
                onClick={() => handleSubTabChange("shop")}
                className={`px-[15px] py-[9px] rounded-[11px] text-[13.5px] whitespace-nowrap flex-shrink-0 transition-colors border ${
                  profileSubTab === "shop"
                    ? "font-semibold bg-zinc-900 border-zinc-900 text-white dark:bg-zinc-100 dark:border-zinc-100 dark:text-zinc-900"
                    : "font-medium bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                }`}
              >
                {t("profile.shopTitle") || "দোকান / ব্যবসা"}
              </button>
            )}
            <button
              type="button"
              onClick={() => handleSubTabChange("password")}
              className={`px-[15px] py-[9px] rounded-[11px] text-[13.5px] whitespace-nowrap flex-shrink-0 transition-colors border ${
                profileSubTab === "password"
                  ? "font-semibold bg-zinc-900 border-zinc-900 text-white dark:bg-zinc-100 dark:border-zinc-100 dark:text-zinc-900"
                  : "font-medium bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              }`}
            >
              {t("profile.changePassword")}
            </button>
          </div>

          {/* ── Personal sub-tab (read-only summary) ── */}
          {profileSubTab === "personal" && !personalEditMode && (
            <PersonalSummaryCard
              user={user}
              t={t}
              onEdit={() => { setPersonalCountry(user?.country || ""); setPersonalEditMode(true); }}
              onVerifyEmail={() => { setEmailToVerify(user?.email || ""); setEmailVerifyOpen(true); }}
            />
          )}

          {/* ── Personal sub-tab (edit form) ── */}
          {profileSubTab === "personal" && personalEditMode && (
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm p-4 sm:p-6">
              <Form
                form={form}
                onFinish={handleProfileUpdate}
                initialValues={{
                  name: user?.name,
                  country: user?.country || "",
                  email: user?.email || "",
                  address: user?.address || "",
                  district: user?.district || "",
                  thana: user?.thana || "",
                  postcode: user?.postcode || "",
                  gender: user?.gender || null,
                  about: user?.about || "",
                  profession: user?.profession || "",
                  dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split("T")[0] : "",
                }}
              >
                <div className="flex flex-col gap-4">
                  {/* Country */}
                  <Field name="country">
                    {(control) => (
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{t("auth.country")}</label>
                        <CountrySelect
                          value={control.value || ""}
                          // Locked once set (identity/verification channel depend on it).
                          disabled={!!user?.country}
                          onChange={(code) => {
                            control.onChange(code);
                            setPersonalCountry(code);
                            // District/thana formats differ (BD dropdown vs free text), so
                            // clear them when the country changes to avoid stale values.
                            form.setFieldValue("district", "");
                            form.setFieldValue("thana", "");
                          }}
                        />
                      </div>
                    )}
                  </Field>

                  {/* Row 1: name + phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field name="name">
                      {(control, meta) => (
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                            {t("auth.fullName")}
                          </label>
                          <input {...control} placeholder={t("profile.enterFullName")} className={`${inputCls} ${fieldHighlightCls(control, meta)}`} />
                          {meta.errors?.[0] && <p className="mt-0.5 text-xs text-red-500">{meta.errors[0]}</p>}
                        </div>
                      )}
                    </Field>
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{t("auth.phoneNumber")}</label>
                      <input type="text" value={user?.phone} disabled className={inputCls} />
                    </div>
                  </div>

                  {/* Row 2: email + profession */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field name="email">
                      {(control, meta) => {
                        const verified = !!user?.emailVerified;
                        return (
                          <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                              {t("profile.email")}
                              {verified && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 text-[10.5px] font-semibold">
                                  <IconCircleCheckFilled size={12} /> {t("profile.emailVerified") || "যাচাইকৃত"}
                                </span>
                              )}
                            </label>
                            <div className="flex items-center gap-2">
                              <input {...control} disabled={verified} placeholder="example@email.com" className={`${inputCls} flex-1 ${verified ? "opacity-70 cursor-not-allowed" : ""} ${fieldHighlightCls(control, meta)}`} />
                              {!verified && (
                                <button
                                  type="button"
                                  onClick={openEmailVerify}
                                  className="flex-none inline-flex items-center gap-1.5 px-3 py-2 border border-primary/40 text-primary text-xs sm:text-sm font-semibold rounded-lg hover:bg-primary/5 transition-colors whitespace-nowrap"
                                >
                                  <IconMailCheck size={15} /> {t("profile.verifyEmail") || "যাচাই করুন"}
                                </button>
                              )}
                            </div>
                            {meta.errors?.[0] && <p className="mt-0.5 text-xs text-red-500">{meta.errors[0]}</p>}
                            {!verified && (
                              <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-500">
                                {t("profile.emailVerifyHint") || "বোনাস পেতে ইমেইল যাচাই করা আবশ্যক।"}
                              </p>
                            )}
                          </div>
                        );
                      }}
                    </Field>
                    <Field name="profession">
                      {(control, meta) => (
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{t("profile.profession")}</label>
                          <input {...control} placeholder={t("profile.professionPlaceholder")} className={`${inputCls} ${fieldHighlightCls(control, meta)}`} />
                          {meta.errors?.[0] && <p className="mt-0.5 text-xs text-red-500">{meta.errors[0]}</p>}
                        </div>
                      )}
                    </Field>
                  </div>

                  {/* Row 3: gender + DOB */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field name="gender">
                      {(control, meta) => (
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{t("profile.gender")}</label>
                          <SelectField
                            value={control.value ?? ""}
                            onChange={(val) => control.onChange({ target: { value: val || null } })}
                            placeholder={t("profile.selectGender")}
                            triggerClassName={`${inputCls} ${fieldHighlightCls(control, meta)}`}
                            options={[
                              { value: "",       label: t("profile.selectGender") },
                              { value: "male",   label: t("profile.male") },
                              { value: "female", label: t("profile.female") },
                              { value: "other",  label: t("profile.other") },
                            ]}
                          />
                          {meta.errors?.[0] && <p className="mt-0.5 text-xs text-red-500">{meta.errors[0]}</p>}
                        </div>
                      )}
                    </Field>
                    <Field name="dateOfBirth">
                      {(control, meta) => (
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{t("profile.dateOfBirth")}</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className={`${inputCls} flex items-center gap-2.5 text-left ${fieldHighlightCls(control, meta)}`}>
                                <IconCalendar size={16} className="text-zinc-400 flex-shrink-0" />
                                <span className={control.value ? "text-zinc-800 dark:text-zinc-100" : "text-zinc-400 dark:text-zinc-500"}>
                                  {control.value
                                    ? format(parse(control.value, "yyyy-MM-dd", new Date()), "dd MMMM yyyy")
                                    : t("profile.selectDateOfBirth") || "জন্ম তারিখ বেছে নিন"}
                                </span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                captionLayout="dropdown"
                                selected={control.value ? parse(control.value, "yyyy-MM-dd", new Date()) : undefined}
                                onSelect={(date) => control.onChange({ target: { value: date ? format(date, "yyyy-MM-dd") : "" } })}
                                fromYear={1950}
                                toYear={new Date().getFullYear() - 5}
                                defaultMonth={control.value ? parse(control.value, "yyyy-MM-dd", new Date()) : new Date(1990, 0)}
                              />
                            </PopoverContent>
                          </Popover>
                          {meta.errors?.[0] && <p className="mt-0.5 text-xs text-red-500">{meta.errors[0]}</p>}
                        </div>
                      )}
                    </Field>
                  </div>

                  {/* Thin divider */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
                    <span className="text-xs text-zinc-400">{t("profile.addressInfo")}</span>
                    <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
                  </div>

                  {/* Address full */}
                  <Field name="address">
                    {(control, meta) => (
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{t("profile.address")}</label>
                        <input {...control} placeholder={t("profile.addressPlaceholder")} className={`${inputCls} ${fieldHighlightCls(control, meta)}`} />
                        {meta.errors?.[0] && <p className="mt-0.5 text-xs text-red-500">{meta.errors[0]}</p>}
                      </div>
                    )}
                  </Field>

                  {/* District / Thana / Postcode — 3 col */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field name="district">
                      {(control, meta) => (
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{t("profile.district")}</label>
                          {(!personalCountry || personalCountry === "BD") ? (
                            <GeoCombobox
                              value={control.value || ""}
                              onChange={(v) => { control.onChange(v); form.setFieldValue("thana", ""); }}
                              options={bdDistricts.map((d) => ({ value: d.id, label: d.bn_name, en: d.name }))}
                              placeholder={t("profile.districtPlaceholder")}
                              className={fieldHighlightCls(control, meta)}
                              selectOnly
                            />
                          ) : (
                            <input {...control} placeholder={t("profile.districtPlaceholder")} className={`${inputCls} ${fieldHighlightCls(control, meta)}`} />
                          )}
                          {meta.errors?.[0] && <p className="mt-0.5 text-xs text-red-500">{meta.errors[0]}</p>}
                        </div>
                      )}
                    </Field>
                    <Field name="thana">
                      {(control, meta) => {
                        const isBdProfile = !personalCountry || personalCountry === "BD";
                        const districtLabel = form.getFieldValue("district") || "";
                        const district = bdDistricts.find((d) => d.bn_name === districtLabel || d.name === districtLabel);
                        const upazilaOptions = district
                          ? bdUpazilas.filter((u) => u.district_id === district.id).map((u) => ({ value: u.id, label: u.bn_name, en: u.name }))
                          : [];
                        return (
                          <div>
                            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{t("profile.thana")}</label>
                            {isBdProfile ? (
                              <GeoCombobox
                                value={control.value || ""}
                                onChange={control.onChange}
                                options={upazilaOptions}
                                placeholder={t("profile.thanaPlaceholder")}
                                className={fieldHighlightCls(control, meta)}
                                selectOnly
                              />
                            ) : (
                              <input {...control} placeholder={t("profile.thanaPlaceholder")} className={`${inputCls} ${fieldHighlightCls(control, meta)}`} />
                            )}
                            {meta.errors?.[0] && <p className="mt-0.5 text-xs text-red-500">{meta.errors[0]}</p>}
                          </div>
                        );
                      }}
                    </Field>
                    {/* Postcode */}
                    <Field name="postcode">
                      {(control, meta) => (
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{t("profile.postcode")}</label>
                          <input {...control} placeholder="e.g. 5800" className={`${inputCls} ${fieldHighlightCls(control, meta)}`} />
                          {meta.errors?.[0] && <p className="mt-0.5 text-xs text-red-500">{meta.errors[0]}</p>}
                        </div>
                      )}
                    </Field>
                  </div>

                  {/* About */}
                  <Field name="about">
                    {(control, meta) => (
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">{t("profile.about")}</label>
                        <textarea
                          {...control}
                          placeholder={t("profile.aboutPlaceholder")}
                          rows={3}
                          className={`${inputCls} resize-y ${fieldHighlightCls(control, meta)}`}
                        />
                        {meta.errors?.[0] && <p className="mt-0.5 text-xs text-red-500">{meta.errors[0]}</p>}
                      </div>
                    )}
                  </Field>

                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setPersonalEditMode(false)}
                      disabled={loading}
                      className="px-4 py-2 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 rounded-lg font-medium text-sm transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-60"
                    >
                      {t("common.cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
                    >
                      {loading && <Spinner size={14} />}
                      {t("profile.updateInfo")}
                    </button>
                  </div>
                </div>
              </Form>
            </div>
          )}

          {/* ── Shop sub-tab ── */}
          {profileSubTab === "shop" && !isOperator && !isAdmin && (
            <ShopSummaryCard
              officeSpace={selectedOfficeSpace}
              t={t}
              onManage={() =>
                selectedOfficeSpace?.id && navigate(`/officespaces/${selectedOfficeSpace.id}/settings`)
              }
              onCreate={() => navigate("/officespaces")}
            />
          )}

          {/* ── Password sub-tab ── */}
          {profileSubTab === "password" && (() => {
            const pwVal = pwLive;
            const rules = [
              { passed: pwVal.length >= 8, label: t("profile.passwordRule1") || "কমপক্ষে ৮টি অক্ষর ব্যবহার করুন" },
              { passed: /[a-z]/.test(pwVal) && /[A-Z]/.test(pwVal), label: t("profile.passwordRule2") || "বড় ও ছোট হাতের অক্ষর মেশান" },
              { passed: /[0-9]/.test(pwVal), label: t("profile.passwordRule3") || "অন্তত একটি সংখ্যা যোগ করুন" },
              { passed: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwVal), label: t("profile.passwordRule4") || "একটি বিশেষ চিহ্ন (!@#) যোগ করুন" },
            ];
            return (
            <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Left: password form */}
              <div className="lg:col-span-3 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm p-4 sm:p-6">
                <h4 className="font-semibold text-base text-zinc-800 dark:text-zinc-100 mb-1">
                  {t("profile.changePassword")}
                </h4>
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-5">
                  {t("profile.passwordChangeSubtitle") || "নিয়মিত পাসওয়ার্ড পরিবর্তন আপনার অ্যাকাউন্ট নিরাপদ রাখে!"}
                </p>
                <Form
                  form={passwordForm}
                  onFinish={handlePasswordChange}
                  onValuesChange={(changed) => {
                    if ("newPassword" in changed) setPwLive(changed.newPassword || "");
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <Field name="currentPassword">
                      {(control, meta) => (
                        <RcPasswordField
                          control={control}
                          meta={meta}
                          label={t("profile.currentPassword")}
                          placeholder="• • • • • • • •"
                          leftIcon={<IconLock size={16} />}
                          required
                        />
                      )}
                    </Field>

                    <Field name="newPassword">
                      {(control, meta) => (
                        <RcPasswordField
                          control={control}
                          meta={meta}
                          label={t("auth.newPassword")}
                          placeholder={t("profile.enterNewPassword")}
                          leftIcon={<IconLock size={16} />}
                          required
                        />
                      )}
                    </Field>

                    <Field name="confirmPassword">
                      {(control, meta) => (
                        <RcPasswordField
                          control={control}
                          meta={meta}
                          label={t("auth.confirmNewPassword")}
                          placeholder={t("profile.confirmNewPassword")}
                          leftIcon={<IconLock size={16} />}
                          required
                        />
                      )}
                    </Field>

                    <label className="flex items-center gap-2 cursor-pointer select-none w-fit pt-1">
                      <input
                        type="checkbox"
                        checked={pwLogoutOthers}
                        onChange={(e) => setPwLogoutOthers(e.target.checked)}
                        className="w-4 h-4 accent-primary rounded"
                      />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">
                        {t("profile.logOutOtherDevices")}
                      </span>
                    </label>

                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        disabled={passwordLoading}
                        className="px-5 py-2.5 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
                      >
                        {passwordLoading && <Spinner size={14} />}
                        {t("profile.updatePassword") || "পাসওয়ার্ড আপডেট করুন"}
                      </button>
                    </div>
                  </div>
                </Form>
              </div>

              {/* Right: password rules sidebar (live validation) */}
              <div className="lg:col-span-2 bg-orange-50 dark:bg-orange-950/20 rounded-xl border border-orange-100 dark:border-orange-900 p-4 sm:p-5 h-fit">
                <h4 className="font-semibold text-base text-orange-800 dark:text-orange-300 mb-4">
                  {t("profile.passwordRulesTitle") || "নিরাপদ পাসওয়ার্ডের নিয়ম"}
                </h4>
                <div className="flex flex-col gap-3">
                  {rules.map((rule, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        rule.passed
                          ? "bg-emerald-500"
                          : "bg-primary/20"
                      }`}>
                        <IconCheck size={14} className={rule.passed ? "text-white" : "text-primary"} />
                      </div>
                      <span className={`text-sm transition-colors ${
                        rule.passed
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-orange-700 dark:text-orange-300"
                      }`}>{rule.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Link to the dedicated account-deletion tab (no destructive UI here) */}
            <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50/50 dark:bg-red-950/20 p-4">
              <div className="flex items-center gap-2 min-w-0">
                <IconAlertTriangle size={18} className="text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600 dark:text-red-400 truncate">
                  {t("profile.deleteAccountLinkHint") || "অ্যাকাউন্ট মুছে ফেলতে চান?"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleSubTabChange("delete")}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-100 dark:hover:bg-red-950/40 transition-colors"
              >
                <IconTrash size={15} />
                {t("profile.deleteAccountTab") || "অ্যাকাউন্ট মুছুন"}
              </button>
            </div>
            </div>
            );
          })()}

          {/* ── Delete account sub-tab (dedicated Danger Zone) ── */}
          {profileSubTab === "delete" && (
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-red-200 dark:border-red-900/60 shadow-sm p-4 sm:p-6 max-w-2xl">
              <div className="flex items-center gap-2 mb-1">
                <IconAlertTriangle size={18} className="text-red-500" />
                <h4 className="font-semibold text-base text-red-600 dark:text-red-400">
                  {t("profile.dangerZone") || "বিপজ্জনক এলাকা"}
                </h4>
              </div>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4 max-w-xl">
                {t("profile.deleteAccountDesc") ||
                  "অ্যাকাউন্ট মুছে ফেললে আপনি আর লগইন করতে পারবেন না এবং প্রবেশাধিকার হারাবেন।"}
              </p>
              <button
                type="button"
                onClick={openDeleteModal}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
              >
                <IconTrash size={16} />
                {t("profile.deleteAccountButton") || "আমার অ্যাকাউন্ট মুছুন"}
              </button>
            </div>
          )}
        </div>
      )}



      {/* Crop modal — shared fullscreen CropStage (same editor as the photo-edit modules) */}
      {cropModalOpen && cropSrc && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-white dark:bg-zinc-900">
          <CropStage
            imageSrc={cropSrc}
            fileName="profile.jpg"
            isMobile={isMobile}
            t={t}
            aspect={1}
            outWidth={512}
            outHeight={512}
            outMaxDim={1024}
            showSkip
            title={t("profile.cropPicture")}
            primaryLabel={t("common.save")}
            onClose={handleCropModalClose}
            onApply={handleCropApply}
            onSkip={handleCropSkip}
            onError={handleCropError}
          />
        </div>
      )}

      {/* Delete Account Modal */}
      {deleteModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => !deleteLoading && setDeleteModalOpen(false)}
        >
          <div
            className="bg-white dark:bg-zinc-900 rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-700">
              <div className="flex items-center gap-2">
                <IconAlertTriangle size={18} className="text-red-500" />
                <h3 className="font-semibold text-sm text-red-600 dark:text-red-400">
                  {t("profile.deleteAccountConfirmTitle") || "অ্যাকাউন্ট মুছে ফেলবেন?"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => !deleteLoading && setDeleteModalOpen(false)}
                className="flex items-center justify-center w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white text-xl leading-none transition-colors"
              >
                &times;
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">
                {t("profile.deleteAccountConfirmDesc") ||
                  "আপনার অ্যাকাউন্ট থেকে স্থায়ীভাবে সবকিছু মুছে যাবে এবং লগআউট হয়ে যাবে। একবার মুছে ফেললে এই একই নম্বর দিয়ে ৯০ দিনের মধ্যে আপনি আর সহজ কাজে যোগ দিতে পারবেন না। নিশ্চিত করতে আপনার পাসওয়ার্ড দিন।"}
              </p>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                {t("profile.deleteAccountPasswordLabel") || "বর্তমান পাসওয়ার্ড"}
              </label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 z-10">
                  <IconLock size={16} />
                </span>
                <input
                  type={showDeletePassword ? "text" : "password"}
                  value={deletePassword}
                  onChange={(e) => {
                    setDeletePassword(e.target.value);
                    if (deleteError) setDeleteError("");
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleDeleteAccount()}
                  placeholder={t("profile.deleteAccountPasswordPlaceholder") || "আপনার পাসওয়ার্ড লিখুন"}
                  autoFocus
                  className={`${inputCls} pl-8 pr-10 ${deleteError ? "border-red-500 focus:ring-red-400" : ""}`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowDeletePassword((v) => !v)}
                  aria-label={showDeletePassword ? t("common.hidePassword") : t("common.showPassword")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                >
                  {showDeletePassword ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                </button>
              </div>
              {deleteError && <p className="mt-1.5 text-xs text-red-500">{deleteError}</p>}
              <div className="flex justify-end gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={deleteLoading}
                  className="px-4 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 disabled:opacity-60"
                >
                  {t("common.cancel") || "বাতিল"}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md text-sm font-medium disabled:opacity-60 flex items-center gap-2"
                >
                  {deleteLoading && <Spinner size={14} />}
                  {t("profile.deleteAccountConfirmButton") || "হ্যাঁ, আমার অ্যাকাউন্ট মুছুন"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
